import { useState } from "preact/hooks";
import { Icon } from "../chrome/icons.js";
import { dayLabel, remainingMinutes, ringLabel } from "../clock.js";
import { newestFirst, type ViewPhoto } from "../photos.js";
import { COVER_ID, DEMO_CODE, formatCode, seedSrc } from "../seed.js";
import type { DemoBrand } from "../types.js";

export interface PanelScreenProps {
  readonly brand: DemoBrand;
  readonly photos: readonly ViewPhoto[];
  readonly guests: number;
  readonly current: number;
  readonly today: Date;
  onInvite(): void;
  onWall(): void;
  onModerate(): void;
  onEditBrand(): void;
}

interface TvSheetProps {
  onHere(): void;
  onClose(): void;
}

function TvSheet({ onHere, onClose }: TvSheetProps) {
  return (
    <div class="d-sheet-backdrop">
      <div class="d-sheet" role="dialog" aria-modal="true" aria-labelledby="demo-tv-title">
        <h2 id="demo-tv-title" class="d-sheet-title">
          Abra o telão no computador da TV
        </h2>
        <p class="d-sheet-body">
          No evento, o telão abre no computador ligado à TV ou ao projetor. Aponte a câmera desse
          computador para o QR ou digite o código no navegador dele.
        </p>
        <div class="d-tv-code">
          <img
            src="/assets/demo/qr.svg"
            alt="QR code do telão de exemplo"
            width={120}
            height={120}
          />
          <p class="d-code">{formatCode(DEMO_CODE)}</p>
        </div>
        <p class="d-caption">Nesta demonstração, o telão abre neste aparelho.</p>
        <button type="button" class="d-button d-primary" onClick={onHere}>
          Ver aqui mesmo
        </button>
        <button type="button" class="d-button d-secondary" onClick={onClose}>
          Fechar
        </button>
      </div>
    </div>
  );
}

export function PanelScreen({
  brand,
  photos,
  guests,
  current,
  today,
  onInvite,
  onWall,
  onModerate,
  onEditBrand,
}: PanelScreenProps) {
  const [tv, setTv] = useState(false);
  return (
    <div class="d-screen d-scroll" data-theme="light" data-testid="screen-panel">
      <header class="d-org-bar">
        <span class="d-org-title">{brand.eventName}</span>
        <button type="button" class="d-link" onClick={onEditBrand}>
          Editar marca
        </button>
      </header>
      <main class="d-panel">
        <img class="d-panel-cover" src={seedSrc(COVER_ID, "s")} alt="" />
        <div class="d-panel-title">
          <h1>{brand.eventName}</h1>
          <span class="d-badge">Ao vivo</span>
        </div>
        <p class="d-panel-meta">
          {dayLabel(today)} · 19h00 – 01h00 · Termina em {ringLabel(remainingMinutes(current))}
        </p>
        <nav class="d-doors" aria-label="Ações do evento">
          <button type="button" class="d-door" data-guide-target="panel" onClick={onInvite}>
            <Icon name="qr" />
            <span>Convite</span>
          </button>
          <button type="button" class="d-door" onClick={() => setTv(true)}>
            <Icon name="monitor" />
            <span>Telão</span>
          </button>
          <button type="button" class="d-door" onClick={onModerate}>
            <Icon name="flag" />
            <span>Moderar</span>
          </button>
        </nav>
        <section class="d-kpis">
          <div class="d-kpi">
            <p class="d-micro">Convidados</p>
            <p>
              <strong>{guests}</strong>
              <span>convidados já mandaram fotos</span>
            </p>
          </div>
          <div class="d-kpi">
            <p class="d-micro">Mídias</p>
            <p>
              <strong>{photos.length}</strong>
              <span>fotos no álbum</span>
            </p>
          </div>
        </section>
        <section>
          <h2 class="d-section-title">
            <span>Fotos recentes</span>
          </h2>
          <div class="d-grid3">
            {newestFirst(photos)
              .slice(0, 6)
              .map((photo) => (
                <img key={photo.id} class="d-tile" src={photo.small} alt="" />
              ))}
          </div>
        </section>
      </main>
      {tv ? (
        <TvSheet
          onHere={() => {
            setTv(false);
            onWall();
          }}
          onClose={() => setTv(false)}
        />
      ) : null}
    </div>
  );
}
